import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { AssetViewModel } from '@app/_models/_viewModels/asset.viewmodel';
import { UploadFile, NzMessageService } from 'ng-zorro-antd';
import { Media } from '@app/_models/_DTOs/media';
import { environment } from '@environments/environment';
import { NgForm } from '@angular/forms';
import { AssetService } from '@app/_services';
import { HttpEventType } from '@angular/common/http';
import { AssetGroup } from '@app/_models/_DTOs/assetGroup';
import { Department } from '@app/_models/_DTOs/department';
import { Supplier } from '@app/_models/_DTOs/supplier';
import { Employee } from '@app/_models/_DTOs/employee';
import { LocationService } from '@app/_services/location.service';
import { DepartmentService } from '@app/_services/department.service';
import { SupplierService } from '@app/_services/supplier.service';
import { EmployeeService } from '@app/_services/employee.service';
import { AssetGroupService } from '@app/_services/assetGroup.service';
import { ResizeService } from '@app/_services/resize.service';
import { DocumentService } from '@app/_services/document.service';
import { Location } from '@app/_models/_DTOs/location';
import { SCREEN_SIZE } from '@app/_helpers/screen-size.enum';
import { AssetHistoryActionType } from '@app/_models/assetHistoryActionType';
import { AssetHistoryActionName } from '@app/_models/_DTOs/assetHistory';
import { Asset, AssetTypeName } from '@app/_models/_DTOs/asset';
import { AssetType } from '@app/_models/enums/assetType';
import { AutonumericOptions } from '@angularfy/autonumeric/lib/autonumeric.model';
import { ZXingScannerComponent } from '@zxing/ngx-scanner';
import { InventoryFoundAssetService } from '@app/_services/inventoryFoundAsset.service';
import { InventoryFoundAsset } from '@app/_models/_DTOs/inventoryFoundAsset';
import { InventoryFoundAssetViewModel } from '@app/_models/_viewModels/inventoryFoundAsset.viewmodel';

@Component({
  selector: "app-found-asset-editor",
  templateUrl: "./found-asset-editor.component.html",
  styleUrls: ["./found-asset-editor.component.css"],
})
export class InventoryFoundAssetEditorComponent implements OnInit {
  @ViewChild("barCodeScanner", { static: false })
  barCodeScanner: ZXingScannerComponent;

  @Input() hiddenFields: string[];
  @Input() disabledFields: string[];
  @Input() viewModel: InventoryFoundAssetViewModel;
  @Input() isEditing: boolean;
  @Input() conflictingAssets: Asset[];
  @Input() conflictingAssetNames: string[];
  @Output() drawerClosed = new EventEmitter<boolean>();

  public createForm: NgForm;

  @ViewChild("inventoryCodeInputElement", { static: false })
  inventoryCodeInputElement?: ElementRef;

  drawerWidth: string;

  isSavingAsset: boolean = false;
  uploadPercentage: number = 0;

  errorMessages = {};

  showUploadList = {
    showPreviewIcon: true,
    showRemoveIcon: true,
    hidePreviewIconInNonImage: true,
  };
  fileList: UploadFile[] = [];
  previewImage: string | undefined = "";
  previewVisible = false;

  apiUrl: string;

  locations: Location[] = [];
  // filteredLocations: Location[] = [];
  assetGroups: AssetGroup[] = [];
  departments: Department[] = [];
  suppliers: Supplier[] = [];
  employees: Employee[] = [];

  public assetTypes = AssetType;

  private delayTimer;

  inventoryCodeOptions: AutonumericOptions = {
    allowDecimalPadding: false,
    decimalPlaces: 0,
    digitGroupSeparator: "",
    leadingZero: "keep",
    maximumValue: "99999",
  };

  purchasePriceOptions: AutonumericOptions = {
    currencySymbol: " EUR",
    currencySymbolPlacement: "s",
    decimalCharacter: ",",
    decimalCharacterAlternative: ".",
    digitGroupSeparator: ".",
    minimumValue: "0",
    negativePositiveSignPlacement: "p",
    outputFormat: "number",
  };

  currentValueOptions: AutonumericOptions = {
    currencySymbol: " EUR",
    currencySymbolPlacement: "s",
    decimalCharacter: ",",
    decimalCharacterAlternative: ".",
    digitGroupSeparator: ".",
    minimumValue: "0",
    negativePositiveSignPlacement: "p",
    outputFormat: "number",
  };

  allowedFormats = [
    "EAN_13",
    "CODE_128",
    "CODABAR",
    "CODE_39",
    "CODE_93",
    "EAN_8",
    "UPC_A",
    "UPC_E",
  ];
  scannerError: string = "";
  barCodeScannerEnabled: boolean = false;

  currencyFormatter = (value: number) => `${value} EUR`;
  currencyParser = (value: string) => value.replace(" EUR", "");

  constructor(
    private foundAssetService: InventoryFoundAssetService,
    private assetService: AssetService,
    private locationService: LocationService,
    private departmentService: DepartmentService,
    private supplierService: SupplierService,
    private employeeService: EmployeeService,
    private assetGroupService: AssetGroupService,
    private messageService: NzMessageService,
    private resizeService: ResizeService,
    private documentService: DocumentService
  ) {
    this.apiUrl = environment.apiUrl;
    this.resizeService.onResize$.subscribe((size) => {
      this.drawerWidth = this.resizeService.getDrawerWidth();
    });
  }

  ngOnInit() {
    this.loadDataForDropdowns();
    if (this.resizeService.currentSize != null) {
      this.drawerWidth = this.resizeService.getDrawerWidth();
    }
  }

  loadDataForDropdowns() {
    this.locationService.getAllBasic().subscribe((data: Location[]) => {
      this.locations = data;
      // this.filteredLocations = data;
    });
    this.employeeService.getAllBasic().subscribe((data: Employee[]) => {
      this.employees = data;
    });
    this.departmentService.getAllBasic().subscribe((data: Department[]) => {
      this.departments = data;
    });
    this.supplierService.getAllBasic().subscribe((data: Supplier[]) => {
      this.suppliers = data;
    });
    this.assetGroupService.getAllBasic().subscribe((data: AssetGroup[]) => {
      this.assetGroups = data;
    });
  }

  handlePreview = (file: UploadFile) => {
    this.previewImage = file.url || file.thumbUrl;
    this.previewVisible = true;
  };

  previewMedia(media: Media) {
    this.previewImage = `${this.apiUrl}/files/${media.path}`;
    this.previewVisible = true;
  }

  beforeUpload = (file: UploadFile): boolean => {
    this.fileList = this.fileList.concat(file);
    return false;
  };

  deleteAssetMedia(assetMediaItem: Media) {
    this.viewModel.media = this.viewModel.media.filter(
      (item) => item !== assetMediaItem
    );
  }

  closeDrawer(form?: NgForm, reload?: boolean) {
    this.fileList = [];
    if (form) {
      form.reset();
    }
    this.viewModel = null;
    if (reload != null) {
      this.drawerClosed.emit(reload);
    }
    this.drawerClosed.emit(false);
  }

  barCodeChange(value: string): void {
    this.viewModel.barCode = value;
    clearTimeout(this.delayTimer);
    let component = this;
    if (value && value.length > 0) {
      this.delayTimer = setTimeout(function () {
        component.assetService
          .getFoundAssetsByBarcode(value, component.viewModel.id)
          .subscribe((assetsWithSameBarcode) => {
            assetsWithSameBarcode.subscribe(
              (conflictingAssetNames) =>
                (component.conflictingAssetNames = conflictingAssetNames)
            );
          });
      }, 500);
    } else {
      this.conflictingAssetNames = [];
    }
  }

  pad(num, size) {
    var s = num + "";
    while (s.length < size) s = "0" + s;
    return s;
  }

  saveAsset(form: NgForm) {
    this.uploadPercentage = 0;
    this.isSavingAsset = true;
    if (this.isEditing) {
      const formData = new FormData();
      this.fileList.forEach((file: any) => {
        formData.append("files", file);
      });
      formData.append("model", JSON.stringify(this.viewModel));

      this.foundAssetService
        .putWithUpload(formData, this.viewModel.id)
        .subscribe(
          (event) => {
            if (event.type === HttpEventType.UploadProgress)
              this.uploadPercentage = Math.round(
                (100 * event.loaded) / event.total
              );
            else if (event.type === HttpEventType.Response) {
              this.messageService.create(
                "success",
                "Inventurni višak uspješno spremljen"
              );
              this.isSavingAsset = false;
              this.uploadPercentage = 0;
              this.closeDrawer(form, true);
            }
          },
          (error) => {
            if (error.error.ParamName) {
              if (form.controls[error.error.ParamName]) {
                form.controls[error.error.ParamName].setErrors({
                  custom: true,
                });
                var errorMessage =
                  error.error.Message.split("Parameter name")[0];
                this.errorMessages[error.error.ParamName] = errorMessage;
              }
            }
            this.isSavingAsset = false;
          }
        );
    } else {
      const formData = new FormData();
      this.fileList.forEach((file: any) => {
        formData.append("files", file);
      });
      formData.append("model", JSON.stringify(this.viewModel));

      this.foundAssetService.postWithUpload(formData).subscribe(
        (event) => {
          if (event.type === HttpEventType.UploadProgress)
            this.uploadPercentage = Math.round(
              (100 * event.loaded) / event.total
            );
          else if (event.type === HttpEventType.Response) {
            this.messageService.create(
              "success",
              "Inventurni višak uspješno spremljen"
            );
            this.isSavingAsset = false;
            this.uploadPercentage = 0;
            this.closeDrawer(form, true);
          }
        },
        (error) => {
          if (error.error.ParamName) {
            if (form.controls[error.error.ParamName]) {
              form.controls[error.error.ParamName].setErrors({ custom: true });
              var errorMessage = error.error.Message.split("Parameter name")[0];
              this.errorMessages[error.error.ParamName] = errorMessage;
            }
          }
          this.isSavingAsset = false;
          this.uploadPercentage = 0;
        }
      );
    }
  }

  enableScanner() {
    this.scannerError = "";
    this.barCodeScannerEnabled = true;
  }

  disableScanner() {
    this.scannerError = "";
    this.barCodeScannerEnabled = false;
  }

  barCodeScanSuccessHandler(scanResult: string) {
    if (scanResult && scanResult.length > 0) {
      this.viewModel.barCode = scanResult;
      let component = this;
      this.delayTimer = setTimeout(function () {
        component.assetService
          .getFoundAssetsByBarcode(scanResult, component.viewModel.id)
          .subscribe((assetsWithSameBarcode) => {
            assetsWithSameBarcode.subscribe((conflictingAssetNames) => {
              component.conflictingAssetNames = conflictingAssetNames;
              if (!assetsWithSameBarcode || conflictingAssetNames.length == 0) {
                this.disableScanner();
              }
            });
          });
      }, 500);
    } else {
      this.conflictingAssetNames = [];
    }
  }

  public getActionTypeName(actionType: AssetHistoryActionType) {
    return AssetHistoryActionName.get(actionType).name;
  }

  public getAssetTypeName(assetType: any) {
    return AssetTypeName.get(assetType);
  }

  public assetTypeKeys() {
    var keys = Object.keys(this.assetTypes);
    return keys.slice(keys.length / 2);
  }

  isFieldHidden(fieldName: string) {
    if (this.hiddenFields) {
      if (this.hiddenFields.length > 0) {
        if (this.hiddenFields.some((x) => x == fieldName)) {
          return false;
        }
      }
    }
    return true;
  }

  isFieldEnabled(fieldName: string) {
    if (this.disabledFields) {
      if (this.disabledFields.length > 0) {
        if (this.disabledFields.some((x) => x == fieldName)) {
          return false;
        }
      }
    }
    return true;
  }
}
